import {
	ReactNode,
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { useSlideToggle } from "../../hooks/useSlideToggle/useSlideToggle";
import { cn } from "../../lib/utils/cn";

const JobListContent = memo(({ children }: { children: ReactNode }) => {
	return children;
});

JobListContent.displayName = "JobListContent";

interface JobListPortalProps {
	children: ReactNode;
	isOpen: boolean;
	targetId?: string;
	onToggle?: (isOpen: boolean) => void;
}

const STORAGE_KEY = "dentlab_job_list_width";
const DEFAULT_WIDTH = 250;
const MIN_WIDTH = 200;
const MAX_WIDTH = 500;

export const JobListPortal: React.FC<JobListPortalProps> = ({
	children,
	isOpen,
	targetId = "job-list-portal",
	onToggle,
}) => {
	const [target, setTarget] = useState<HTMLElement | null>(null);
	const { containerRef } = useSlideToggle(isOpen, onToggle);
	const [width, setWidth] = useState(() => {
		const saved = localStorage.getItem(STORAGE_KEY);
		if (!saved) return DEFAULT_WIDTH;
		const parsed = parseInt(saved, 10);
		return Number.isFinite(parsed)
			? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, parsed))
			: DEFAULT_WIDTH;
	});
	const widthRef = useRef(width);
	const isResizingRef = useRef(false);

	useEffect(() => {
		setTarget(document.getElementById(targetId));
	}, [targetId]);

	useEffect(() => {
		if (target && !isResizingRef.current) {
			target.style.transition = "width 300ms ease-in-out";
			target.style.width = isOpen ? `${width}px` : "0px";
			target.style.overflow = "hidden";
		}
	}, [isOpen, target, width]);

	const handleResizeStart = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			if (!target) return;
			isResizingRef.current = true;
			target.style.transition = "none";

			const handleMouseMove = (e: MouseEvent) => {
				const rect = target.getBoundingClientRect();
				const newWidth = Math.min(
					MAX_WIDTH,
					Math.max(MIN_WIDTH, e.clientX - rect.left)
				);
				widthRef.current = newWidth;
				target.style.width = `${newWidth}px`;
			};

			const handleMouseUp = () => {
				isResizingRef.current = false;
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
				document.body.style.userSelect = "";
				document.body.style.cursor = "";
				target.style.transition = "width 300ms ease-in-out";
				setWidth(widthRef.current);
				localStorage.setItem(
					STORAGE_KEY,
					String(Math.round(widthRef.current))
				);
			};

			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
			document.body.style.userSelect = "none";
			document.body.style.cursor = "col-resize";
		},
		[target]
	);

	const content = (
		<div
			ref={containerRef}
			className={cn(
				"h-full relative transition-all duration-300 ease-in-out",
				isOpen ? "opacity-100" : "opacity-0"
			)}
			aria-hidden={!isOpen}
		>
			<div className="h-full transform origin-left transition-transform duration-300 ease-in-out">
				<JobListContent>{children}</JobListContent>
			</div>
			{isOpen && (
				<div
					className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 transition-colors"
					onMouseDown={handleResizeStart}
					aria-label="Auftragsleiste Breite ändern"
					data-testid="job-list-resize-handle"
				/>
			)}
		</div>
	);

	return target ? createPortal(content, target) : null;
};
